import React from 'react';
import {
  ShieldCheck,
  Lock,
  KeyRound,
  ArrowRight,
  LogIn,
  CheckCircle2,
  Shield,
  Layers,
  Scale,
  Sparkles,
  Database,
  Building2
} from 'lucide-react';

interface LoginScreenProps {
  onLogin: () => Promise<void>;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({ onLogin }) => {
  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row bg-slate-950 text-slate-100 selection:bg-dhristhi selection:text-slate-950">
      
      {/* ========================================================================= */}
      {/* LEFT HALF (50%): Dhristhi Brand, Hero Graphic, and Statutory Highlights  */}
      {/* ========================================================================= */}
      <div className="relative w-full lg:w-1/2 flex flex-col justify-between p-6 sm:p-10 lg:p-14 bg-gradient-to-br from-slate-950 via-slate-900 to-[#041a10] border-b lg:border-b-0 lg:border-r border-slate-800/80 overflow-hidden">
        
        {/* Ambient Decorative Background Glows */}
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-dhristhi/15 blur-3xl pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-dhristhi/5 via-transparent to-transparent pointer-events-none" />

        {/* Top Header: Brand Identity */}
        <div className="relative z-10 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Dhristhi Icon */}
              <div className="w-11 h-11 rounded-xl bg-dhristhi/15 border border-dhristhi/40 flex items-center justify-center p-2 shadow-lg shadow-dhristhi/10 backdrop-blur-md">
                <img
                  src="/brand/dhristhi_icon.svg"
                  alt="Dhristhi Logo"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    // Fallback to SVG Shield if static asset path fails
                    e.currentTarget.style.display = 'none';
                    const parent = e.currentTarget.parentElement;
                    if (parent) parent.innerHTML = '<span class="text-dhristhi font-black text-xl">D</span>';
                  }}
                />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-extrabold tracking-tight text-white">
                    dhristhi
                  </span>
                  <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-dhristhi/20 text-dhristhi-light border border-dhristhi/30">
                    DPDP Suite
                  </span>
                </div>
                <p className="text-[11px] text-slate-400 font-medium">
                  System Pvt. Ltd. • Compliance Cloud
                </p>
              </div>
            </div>

            <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-400 bg-slate-900/80 px-3 py-1.5 rounded-full border border-slate-800 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-dhristhi animate-pulse" />
              <span className="font-mono text-[11px]">DPDP Act 2023 Ready</span>
            </div>
          </div>
        </div>

        {/* Center: Hero Value Proposition & Compliance Graphic */}
        <div className="relative z-10 my-8 lg:my-0 space-y-6 max-w-xl">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-dhristhi/10 border border-dhristhi/30 text-dhristhi-light text-xs font-semibold">
              <Scale className="w-3.5 h-3.5" />
              <span>Statutory Decision &amp; Systems Mapping Workspace</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl xl:text-5xl font-black text-white tracking-tight leading-[1.15]">
              Defensible DPDP Compliance for <span className="text-transparent bg-clip-text bg-gradient-to-r from-dhristhi-light via-emerald-400 to-teal-300">Data Fiduciaries</span>
            </h1>
            
            <p className="text-sm sm:text-base text-slate-300 leading-relaxed font-normal">
              A structured enterprise engine mapping statutory obligations, technical controls, data principles rights, and court-ready audit trails for India&apos;s digital privacy framework.
            </p>
          </div>

          {/* Hero Visual Card */}
          <div className="relative rounded-2xl overflow-hidden border border-slate-800/90 bg-slate-900/60 shadow-2xl group backdrop-blur-md">
            <div className="aspect-[16/9] w-full relative overflow-hidden bg-slate-950">
              <img
                src="/brand/dpdp_hero.jpg"
                alt="DPDP Act Compliance Engine Showcase"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700 opacity-90"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
              
              <div className="absolute bottom-3 left-3 right-3 flex flex-wrap items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 rounded-lg bg-slate-900/90 border border-slate-700/80 text-white font-semibold flex items-center gap-1.5 shadow-md backdrop-blur-sm">
                    <Layers className="w-3 h-3 text-dhristhi" />
                    <span>9 Statutory Tranches</span>
                  </span>
                  <span className="px-2.5 py-1 rounded-lg bg-slate-900/90 border border-slate-700/80 text-white font-semibold flex items-center gap-1.5 shadow-md backdrop-blur-sm">
                    <ShieldCheck className="w-3 h-3 text-emerald-400" />
                    <span>Controls Mapping</span>
                  </span>
                </div>
                <span className="px-2.5 py-1 rounded-lg bg-dhristhi/20 border border-dhristhi/40 text-dhristhi-light font-bold flex items-center gap-1 shadow-md backdrop-blur-sm">
                  <Database className="w-3 h-3" />
                  <span>Local Vault</span>
                </span>
              </div>
            </div>
          </div>

          {/* Key Capabilities Badges */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-slate-900/50 border border-slate-800/80">
              <CheckCircle2 className="w-4 h-4 text-dhristhi shrink-0 mt-0.5" />
              <div className="text-xs">
                <p className="font-semibold text-white">Rule &amp; Penalty Taxonomies</p>
                <p className="text-slate-400 text-[11px]">Mapped against Section 4 to 17 &amp; Schedule 1 liabilities.</p>
              </div>
            </div>

            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-slate-900/50 border border-slate-800/80">
              <CheckCircle2 className="w-4 h-4 text-dhristhi shrink-0 mt-0.5" />
              <div className="text-xs">
                <p className="font-semibold text-white">Role-Based Governance</p>
                <p className="text-slate-400 text-[11px]">Tailored for DPOs, General Counsel, CISOs, and Architects.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Footer: Trust and Assurance */}
        <div className="relative z-10 pt-6 border-t border-slate-800/60 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-slate-500" />
            <span>Built by <strong>Dhristhi System Pvt. Ltd.</strong></span>
          </div>
          <div className="flex items-center gap-3 font-mono text-[11px] text-slate-500">
            <span>PKCE S256</span>
            <span>•</span>
            <span>Zero-Knowledge Cache</span>
            <span>•</span>
            <span>v1.0</span>
          </div>
        </div>

      </div>

      {/* ========================================================================= */}
      {/* RIGHT HALF (50%): Authentication Terminal & Keycloak Single Sign-On CTA   */}
      {/* ========================================================================= */}
      <div className="relative w-full lg:w-1/2 flex flex-col justify-center items-center p-6 sm:p-12 lg:p-16 bg-slate-950 text-slate-100">
        
        {/* Subtle radial glow */}
        <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full bg-dhristhi/10 blur-3xl pointer-events-none" />

        <div className="max-w-md w-full space-y-8 relative z-10">
          
          {/* Mobile-Only Header Brand Logo */}
          <div className="lg:hidden flex items-center justify-center gap-2.5 pb-2">
            <div className="w-9 h-9 rounded-xl bg-dhristhi/15 border border-dhristhi/40 flex items-center justify-center p-1.5">
              <img
                src="/brand/dhristhi_icon.svg"
                alt="Dhristhi Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="text-xl font-bold text-white tracking-tight">
              dhristhi
            </span>
          </div>

          {/* Main Card Icon Header */}
          <div className="text-center space-y-3">
            <div className="relative inline-flex items-center justify-center">
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-tr from-dhristhi-dark via-dhristhi to-emerald-400 flex items-center justify-center text-white shadow-2xl shadow-dhristhi/30">
                <ShieldCheck className="w-10 h-10" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-slate-950 border-2 border-slate-800 flex items-center justify-center text-emerald-400 shadow-md">
                <Lock className="w-3.5 h-3.5" />
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
                DPDP Obligations Register
              </h2>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                Sign in with your corporate single sign-on credentials to access your organization&apos;s decision workspace.
              </p>
            </div>
          </div>

          {/* Identity Provider Security Box */}
          <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800/90 text-left space-y-3 shadow-lg backdrop-blur-md">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-2 text-slate-400 font-medium">
                <KeyRound className="w-4 h-4 text-dhristhi" />
                Identity Provider
              </span>
              <span className="font-mono text-[11px] font-bold text-dhristhi-light bg-dhristhi/15 px-2 py-0.5 rounded-md border border-dhristhi/30">
                auth.dhristhi.app
              </span>
            </div>

            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-2 text-slate-400 font-medium">
                <Shield className="w-4 h-4 text-emerald-400" />
                Security Protocol
              </span>
              <span className="text-[11px] font-mono font-medium text-emerald-400">
                OIDC PKCE (S256)
              </span>
            </div>

            <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-800">
              <span className="flex items-center gap-2 text-slate-400 font-medium">
                <Sparkles className="w-4 h-4 text-amber-400" />
                Target Realm
              </span>
              <span className="font-mono text-[11px] text-slate-300">
                dpdp-dhristhi
              </span>
            </div>
          </div>

          {/* Action Button: Sign In with Keycloak */}
          <div className="space-y-4 pt-1">
            <button
              onClick={onLogin}
              className="w-full py-4 px-6 rounded-2xl font-bold text-sm sm:text-base text-slate-950 bg-gradient-to-r from-dhristhi-light via-dhristhi to-emerald-400 hover:brightness-110 active:scale-[0.98] transition-all flex items-center justify-center gap-3 shadow-xl shadow-dhristhi/30 cursor-pointer group"
            >
              <LogIn className="w-5 h-5 text-slate-950 group-hover:translate-x-0.5 transition-transform" />
              <span>Sign In with Dhristhi Auth</span>
              <ArrowRight className="w-5 h-5 text-slate-950 group-hover:translate-x-1 transition-transform ml-auto" />
            </button>

            <div className="flex items-center justify-center gap-2 text-xs text-slate-400 font-medium">
              <Lock className="w-3.5 h-3.5 text-dhristhi" />
              <span>Enterprise Single Sign-On (SSO) Session Protected</span>
            </div>
          </div>

          {/* Enterprise Compliance Assurance Checklist */}
          <div className="p-4 rounded-xl bg-slate-900/40 border border-slate-800/60 text-xs text-slate-400 space-y-2">
            <p className="font-semibold text-slate-300">Statutory Access Notice:</p>
            <ul className="space-y-1.5 text-[11px] text-slate-400 list-none">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-dhristhi shrink-0" />
                <span>Access is restricted to authorized Data Protection Officers and compliance personnel.</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-dhristhi shrink-0" />
                <span>All assessment decisions and controls modifications are signed into local audit logs.</span>
              </li>
            </ul>
          </div>

          {/* Copyright & Compliance Footer */}
          <div className="pt-2 text-center text-[11px] text-slate-400 space-y-1">
            <p>© 2026 Dhristhi System Pvt. Ltd. All rights reserved.</p>
            <p className="text-[10px] text-slate-400">
              India Digital Personal Data Protection Framework Statutory Suite
            </p>
          </div>

        </div>

      </div>

    </div>
  );
};
